import { getJSON, getText } from '@storebrand-digital/fms-translations';

import {
  AuroraChildCompleteDetails,
  ChildDetails,
  InsuranceDetails,
  NewChildDetails,
} from 'Api/insurance';
import {
  HomeBuyerDetails,
  HomeBuyerFacade,
} from 'Api/insurance/types/products/homeBuyer.types';
import { CoverageTableProps } from 'Components/CoverageSection/components/CoverageTable';
import { CoverageView } from 'Components/CoverageSection/coverageSection.types';
import { ProductName } from 'Constants/products.constants';
import {
  AkaStudentCoverage,
  CarCoverage,
} from 'Types/coverages/coverages.types';
import {
  isAkaStudentOutsideNordicCountries,
  isFireTheftCaravanCoverage,
  isKaskoCaravanCoverage,
  isStandardTravelCoverage,
  isSuperTravelCoverage,
} from 'Utils/coverages.utils';
import { formatMoney } from 'Utils/formatting/formatting.utils';
import { getFMSInsuranceName } from 'Utils/text/text.utils';
import {
  MEDICAL_DISABILITY_COVERAGE_FULL_TIME,
  OWN_CONTRIBUTION_FALLBACK_FMS_KEY,
} from '../insuranceDetailsSections.constants';
import { LabelValuePair } from '../insuranceDetailsSections.types';

export function getFMSCoverageName(coverage: string): string {
  if (coverage === null) {
    return getText('detailed.secondary.coverage.fallback');
  }
  return getText(
    `detailed.secondary.coverage.${coverage.replace(/\s|\+/g, '')}`, // TODO: check this
    { insert: { coverage } },
  );
}

export function makeCoverageSection(
  cov: CoverageView,
): Array<CoverageTableProps> {
  return [
    {
      coverages: cov,
      description: getText('insurance.coverage.description'),
      iconName: 'check',
      iconColor: 'green',
    },
  ];
}

/** Defined coverage is where the coverage is a single string */
export function setDefinedCoverageSection(
  insurance: InsuranceDetails,
  coverageKey?: string,
): Array<CoverageTableProps> {
  const insuranceCoverage = coverageKey ?? insurance.coverage;
  const coveragePairsFMSKey = getCoveragePairsFMSKey(
    insurance,
    insuranceCoverage,
  );
  let coverageView = getJSON<CoverageView>(coveragePairsFMSKey);

  if ('isDeregistered' in insurance && insurance.isDeregistered) {
    coverageView = filterNotCoveredForDeregisteredVehicle(coverageView);
  }

  return makeCoverageSection(coverageView);
}

function filterNotCoveredForDeregisteredVehicle(
  coverageView: CoverageView,
): CoverageView {
  const filteredCoverageView = Object.entries(coverageView).map(
    ([language, coverages]) => {
      const filtered = coverages.filter(
        (cov) => !cov.notCoveredForDeregistered,
      );
      return [language, filtered];
    },
  );

  return Object.fromEntries(filteredCoverageView);
}

export function filterNotCoveredForCat(
  coverageView: CoverageView,
): CoverageView {
  const filteredCoverageView = Object.entries(coverageView).map(
    ([language, coverages]) => {
      const filtered = coverages.filter((cov) => !cov.notCoveredForCat);
      return [language, filtered];
    },
  );

  return Object.fromEntries(filteredCoverageView);
}

/* Undefined coverages is where the coverage is an array */
export function setUndefinedCoverageSection(
  insurance: InsuranceDetails,
  coverages: Array<string>,
): Array<CoverageTableProps> {
  const name = getFMSInsuranceName(insurance.insuranceProduct);
  const coverage = filterCoverages(coverages);

  let coverageView = getJSON<CoverageView>(
    `insurance.coverage.${name}.${coverage}.json`,
  );

  if ('isDeregistered' in insurance && insurance.isDeregistered) {
    coverageView = filterNotCoveredForDeregisteredVehicle(coverageView);
  }

  if (
    insurance.insuranceProduct === ProductName.ANIMAL &&
    insurance.animalType === 'CAT'
  ) {
    coverageView = filterNotCoveredForCat(coverageView);
  }

  return makeCoverageSection(coverageView);
}

/* Combines coverage texts to build a section relevant to the covers of the insurance */
export function setCombinedUndefinedCoverageSection(
  insuranceProduct: ProductName,
  coverages: string[],
): Array<CoverageTableProps> {
  const filteredCoverages = coverages.filter(
    (coverage) => coverage in coverageToFmsKeyMap,
  );
  const cov = filteredCoverages.reduce<CoverageView>(
    (prev, coverageKey) => {
      if (!Object.keys(coverageToFmsKeyMap).includes(coverageKey)) {
        return prev;
      }
      const name = getFMSInsuranceName(insuranceProduct);
      const coverage = coverageToFmsKeyMap[coverageKey];
      const coverageTexts = getJSON<CoverageView>(
        `insurance.coverage.${name}.${coverage}.json`,
      );
      const { no = [], en = [] } = coverageTexts;
      return {
        no: [...prev.no, ...no],
        en: [...prev.en, ...en],
      };
    },
    {
      no: [],
      en: [],
    },
  );

  return makeCoverageSection(cov);
}

function getCoveragePairsFMSKey(
  insuranceDetails: InsuranceDetails,
  coverageKey: string,
): string {
  const { insuranceProduct } = insuranceDetails;
  const name = getFMSInsuranceName(insuranceProduct);

  if (insuranceProduct === ProductName.HOME_BUYER_HOUSE) {
    const homeBuyerDetails = insuranceDetails as HomeBuyerDetails;

    if (isHouseFacade(homeBuyerDetails.homeBuyerFacade)) {
      return `insurance.coverage.house.super.json`;
    }
    return `insurance.coverage.cabin.super.json`;
  }

  if (insuranceProduct === ProductName.HOME_BUYER_HOUSE_CONTENTS) {
    const homeBuyerDetails = insuranceDetails as HomeBuyerDetails;

    if (isHouseFacade(homeBuyerDetails.homeBuyerFacade)) {
      return `insurance.coverage.houseContents.super.json`;
    }

    return `insurance.coverage.cabinContents.super.json`;
  }

  if (insuranceProduct === ProductName.HOUSE_CONTENTS_STB_AGREEMENT) {
    return `insurance.coverage.house.super.json`;
  }
  if (insuranceProduct === ProductName.AKA_TRAVEL && coverageKey) {
    return 'insurance.coverage.akaTravel.super.json';
  }
  if (insuranceProduct === ProductName.CAR) {
    if (
      insuranceDetails.rentalCarIncluded &&
      insuranceDetails.coverage !== CarCoverage.FULL_SUPER
    ) {
      return `insurance.coverage.${name}.${coverageKey.replace(/\s/g, '')}.with.rental.car.json`;
    }
    return `insurance.coverage.${name}.${coverageKey.replace(/\s/g, '')}.json`;
  }
  if (insuranceProduct === ProductName.AURORA_DISABILITY_INTEREST) {
    return `insurance.coverage.${name}.json`;
  }
  if (insuranceProduct === ProductName.AURORA_DISABILITY_TOTAL && coverageKey) {
    return `insurance.coverage.${name}.${coverageKey.replace(/\s/g, '')}.json`;
  }
  if (isSuperTravelCoverage(coverageKey)) {
    return 'insurance.coverage.travel.super.json';
  }
  if (isStandardTravelCoverage(coverageKey)) {
    return 'insurance.coverage.travel.standard.json';
  }
  if (isKaskoCaravanCoverage(coverageKey)) {
    return 'insurance.coverage.caravan.kasko.json';
  }
  if (isFireTheftCaravanCoverage(coverageKey)) {
    return 'insurance.coverage.caravan.fireTheft.json';
  }
  if (isAkaStudentOutsideNordicCountries(coverageKey)) {
    return 'insurance.coverage.akaStudent.world.json';
  }
  if (coverageKey === AkaStudentCoverage.TRAVEL_SUPER) {
    return 'insurance.coverage.akaStudent.norway.json';
  }

  return getFMSInsuranceCoveragesKey(name, coverageKey, insuranceProduct);
}

export const isHouseFacade = (homeBuyerFacade: HomeBuyerFacade): boolean =>
  homeBuyerFacade !== HomeBuyerFacade.RECREATIONAL_RESIDENCE;

export function setCoverageFromCoverages(coverages: Array<string>): string {
  const coverage = filterCoverages(coverages);
  return getText(`detailed.secondary.coverage.${coverage}`);
}

const coverageToFmsKeyMap: { [key: string]: string } = {
  Kasko: 'kasko',
  Delkasko: 'delkasko',
  Ansvar: 'ansvar',
  'Fører- og passasjerulykke': 'fop',
  'Brann og tyveri': 'fireTheft',
  'Død, katt, Veterinærutgifter, cat': 'deathAndVeterinarian',
  'Død og bruksverdi, Veterinærutgifter, hund': 'deathAndVeterinarian',
  'Veterinærutgifter, cat': 'veterinarian',
  'Veterinærutgifter, hund': 'veterinarian',
  'Død, katt': 'death',
  'Død og bruksverdi': 'death',
  'A+ Innbo': 'super',
  'A+ Naturskade': 'super',
  'A+ Dødsfall': 'akaGroupLifeDeath',
  'A+ Uførekapital': 'akaGroupLifeDisability',
  'A+ Tidsbegrenset uførerente': 'akaGroupLifeTimeLimitedDisabilityInterest',
  'A+ Dødsfall medforsikret': 'akaGroupLifeCoInsuredDeath',
  'A+ Uførekapital medforsikret': 'akaGroupLifeCoInsuredWorkDisability',
  'A+ Tidsbegrenset uførerente medforsikret':
    'akaGroupLifeCoInsuredTimeLimitedDisabilityInterest',
  'A+ Kritisk Sykdom': 'akaCriticalIllness',
  'A+ Kritisk Sykdom medforsikret': 'akaCriticalIllnessCoInsured',
  'A+ Familieulykke død': 'akaFamilyAccidentDeath',
  'A+ Medisinsk invaliditet': 'akaFamilyAccidentDisability',
  'A+ Medisinsk invaliditet med faginvaliditet':
    'akaFamilyAccidentProfessionalDisability',
  'Uførhet Total UTKN': 'auroraTotal',
  'Uførhet Total mann UKFK-M': 'auroraTotal',
  'Uførhet Total kvinne UKFK-K': 'auroraTotal',
  'Uførhet Basis mann UKFB-M': 'auroraBasis',
  'Uførhet Basis kvinne UKFB-K': 'auroraBasis',
  'Arbeidsuførhet mann UKF': 'auroraWorkDisability',
  'Arbeidsuførhet kvinne UKF': 'auroraWorkDisability',
  Gravid: 'pregnant',
  'Gravid, flere barn': 'pregnantMultipleChildren',
  Nyfødt: 'newBorn',
  'Nyfødt, flere barn': 'newBornMultipleChildren',
  Død: 'accidentDeath',
  'Død, heltid': 'accidentDeathFullTime',
  'Medisinsk invaliditet': 'medicalDisability',
  [MEDICAL_DISABILITY_COVERAGE_FULL_TIME]: 'medicalDisabilityFullTime',
  'Barne- og ungdomsforsikring': 'auroraChildAndYouth',
  Barneulykke: 'auroraChildAccident',
  'Ungdom Pluss': 'auroraYouthPlus',
};

/* translates undefined coverages array into a string key used in fms */
export function filterCoverages(coverages: Array<string>): string {
  const coverageMapKeys = Object.keys(coverageToFmsKeyMap);
  const coverageMapKey = coverageMapKeys.find((key) => coverages.includes(key));

  return coverageMapKey ? coverageToFmsKeyMap[coverageMapKey] : 'fallback';
}

export function getFMSInsuranceCoveragesKey(
  insuranceName: string,
  translatedCoverage: string,
  insuranceProduct: ProductName,
): string {
  if (!translatedCoverage) {
    console.error(
      `getFMSInsuranceCoveragesKey called with undefined coverage, insuranceProduct: ${insuranceProduct}`,
    );
    return `-`;
  }
  return `insurance.coverage.${insuranceName}.${translatedCoverage.replace(
    /\s/g,
    '',
  )}.json`;
}

export function setMultipleCoverages(coverages: Array<string>): string {
  const filteredCoverages = coverages.filter(
    (coverage) => coverage in coverageToFmsKeyMap,
  );
  const translatedCoverages = filteredCoverages.map((coverage) => {
    const coverageKey = coverageToFmsKeyMap[coverage];
    return getText(`detailed.secondary.coverage.${coverageKey}`);
  });
  return translatedCoverages.join(', ');
}

export function setCoverageAmount(
  coverageAmount: number | undefined,
  labelSuffix?: string,
): LabelValuePair {
  const labelKey = labelSuffix
    ? `detailed.secondary.coverageAmount.label.${labelSuffix}`
    : 'detailed.secondary.coverageAmount.label';

  return {
    label: getText(labelKey),
    value: coverageAmount
      ? getText('detailed.secondary.coverageAmount.value', {
          insert: { coverageAmount: formatMoney(coverageAmount) },
        })
      : getText(OWN_CONTRIBUTION_FALLBACK_FMS_KEY),
  };
}

export const getCoverageDetails = (fmsKey: string): CoverageView => {
  const coverage = getJSON<CoverageView>(fmsKey);

  return {
    no: coverage.no,
    en: coverage.en,
  };
};

export const getChildFmsProductName = (
  insurance: ChildDetails | NewChildDetails | AuroraChildCompleteDetails,
): string => {
  const { age, insuranceProduct } = insurance;
  if (age < 0 && window.env.ENVIRONMENT !== 'production') {
    // eslint-disable-next-line no-console
    console.error(`Invalid age set on insurance: ${age}`);
  }

  let fmsProductName = getFMSInsuranceName(insuranceProduct);
  if (age >= 18) {
    fmsProductName = `${fmsProductName}.ofLegalAge`;
  }
  return fmsProductName;
};
